/**
 * State Style Registry - state 顯示元件
 * 用於 Component Styles 預覽 states（感測值、狀態）
 */
import type { ComponentType } from 'react'
import type { StateControlProps } from './stateStyleTypes'
import { StateStyle01 } from './StateStyle01'
import { StateStyle02 } from './StateStyle02'
import { StateStyle03 } from './StateStyle03'
import { StateStyle04 } from './StateStyle04'
import { StateStyle04ThermostatMode } from './StateStyle04ThermostatMode'
import { StateStyle05 } from './StateStyle05'

export type StateStyleRegistry = Record<string, Record<string, ComponentType<StateControlProps>>>

export const STATE_STYLE_DESCRIPTIONS: Record<string, string> = {
  Style01: 'State card: icon + label + value + lifecycle',
  Style02: 'Compact row: icon + value + lifecycle',
  Style03: 'Vertical tile: icon + value + lifecycle',
  Style04: 'Hero card (presence/thermostat mode)',
  Style05: 'Lux card: illuminance + dark/bright meter',
}

const STATE_STYLES = {
  Style01: StateStyle01,
  Style02: StateStyle02,
  Style03: StateStyle03,
  Style04: StateStyle04,
  Style05: StateStyle05,
}

function normalizeStateKey(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
}

function isPresenceState(stateKey: string): boolean {
  const token = normalizeStateKey(stateKey)
  return (
    token.includes('detected') ||
    token.includes('occupancy') ||
    token.includes('presence') ||
    token.includes('motion')
  )
}

function isLuxState(stateKey: string): boolean {
  const token = normalizeStateKey(stateKey)
  return token.includes('lux') || token.includes('illuminance') || token.includes('lightlevel')
}

function isThermostatModeState(stateKey: string): boolean {
  const token = normalizeStateKey(stateKey)
  return token.includes('thermostatmode') || token === 'hvacmode' || token === 'mode'
}

/** state style 依 stateKey 類型提供：presence/mode=Style04, lux=Style05 */
export const STATE_STYLE_REGISTRY: StateStyleRegistry = {}
const BASE_STYLE_IDS: Array<keyof typeof STATE_STYLES> = ['Style01', 'Style02', 'Style03']

export function getStateStyleDescription(styleId: string): string {
  return STATE_STYLE_DESCRIPTIONS[styleId] ?? styleId
}

export function getStateStyleComponent(
  stateKey: string,
  styleId: string
): ComponentType<StateControlProps> | null {
  const normalized = styleId?.trim() || 'Style01'
  if (normalized === 'Style04' && isThermostatModeState(stateKey)) return StateStyle04ThermostatMode
  if (normalized === 'Style04' && isLuxState(stateKey)) return StateStyle05
  if (normalized === 'Style05' && isPresenceState(stateKey)) return StateStyle04
  return STATE_STYLES[normalized as keyof typeof STATE_STYLES] ?? StateStyle01
}

export function getStyleIdsForState(stateKey: string): string[] {
  const styleIds = [...BASE_STYLE_IDS]
  if (isPresenceState(stateKey) || isThermostatModeState(stateKey)) styleIds.push('Style04')
  if (isLuxState(stateKey)) styleIds.push('Style05')
  return styleIds
}

export function listStateStyles(states: string[]): Array<{ stateKey: string; styleId: string; name: string }> {
  const result: Array<{ stateKey: string; styleId: string; name: string }> = []
  for (const stateKey of states) {
    result.push({ stateKey, styleId: 'Style01', name: stateKey })
  }
  return result
}
